import React, { useState, useEffect, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AuthContext } from '../../features/auth/AuthProvider';
import { managedLocations, whereLocation } from '../../features/auth/managedLocations';
import { db } from '../../shared/utils/firebase';
import { collection, doc, getDoc, getDocs, query, where, addDoc, updateDoc, deleteDoc, serverTimestamp, onSnapshot, orderBy, increment } from 'firebase/firestore';
import { LoadingSpinner } from '../../shared/components';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Tabs, 
  Tab, 
  Grid, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  IconButton, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  Chip,
  Snackbar,
  Alert,
  Badge,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardActions,
  Divider,
  Tooltip,
  Collapse,
  Checkbox,
  Menu,
  Avatar,
  Fade,
  Zoom,
  Grow,
  createTheme,
  ThemeProvider,
  CssBaseline,
  useTheme,
  alpha
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Archive as ArchiveIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Person as PersonIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  PriorityHigh as PriorityHighIcon,
  Schedule as ScheduleIcon,
  Label as LabelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckBox as CheckBoxIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Keyboard as KeyboardIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  AccountCircle as AccountCircleIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon
} from '@mui/icons-material';

interface Note {
  id: string;
  content: string;
  created_at: any;
  updated_at: any;
  user_id: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  archived: boolean;
  assigned_to: string;
  assigned_by: string;
  created_at: any;
  updated_at: any;
  due_date: any;
  hidden_by?: string[]; // Array of user IDs who have hidden this task from their view
  priority?: 'critical' | 'high' | 'medium' | 'low';
  category?: string;
  comment_count?: number;
}

interface TaskComment {
  id: string;
  task_id: string;
  user_id: string;
  user_name: string;
  content: string;
  created_at: any;
  edited?: boolean;
  edited_at?: any;
}

interface FilterState {
  search: string;
  status: 'all' | 'active' | 'completed' | 'overdue';
  priority: 'all' | 'critical' | 'high' | 'medium' | 'low';
  assignee: string;
  dateRange: 'all' | 'today' | 'week' | 'month' | 'overdue';
  category: string;
}

// Predefined categories with colors
const TASK_CATEGORIES = [
  { id: 'work', label: 'Work', color: '#2196f3' },
  { id: 'personal', label: 'Personal', color: '#4caf50' },
  { id: 'urgent', label: 'Urgent', color: '#f44336' },
  { id: 'project', label: 'Project', color: '#9c27b0' },
  { id: 'meeting', label: 'Meeting', color: '#ff9800' },
  { id: 'review', label: 'Review', color: '#795548' },
];


interface User {
  uid: string;
  displayName: string;
  email: string;
  role: string;
  assigned_location?: string;
}

// Helper function to check if a task is overdue
const isTaskOverdue = (task: Task): boolean => {
  if (!task.due_date || task.completed || task.archived) return false;
  
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Start of today
  
  let dueDate: Date;
  if (task.due_date.toDate && typeof task.due_date.toDate === 'function') {
    dueDate = task.due_date.toDate();
  } else if (task.due_date.seconds !== undefined) {
    dueDate = new Date(task.due_date.seconds * 1000);
  } else {
    try {
      dueDate = new Date(task.due_date);
    } catch (e) {
      return false;
    }
  }
  
  dueDate.setHours(0, 0, 0, 0);
  return dueDate < now;
};

// Helper function to get priority color
const getPriorityColor = (priority?: string): string => {
  switch (priority) {
    case 'critical': return '#d32f2f';
    case 'high': return '#f57c00';
    case 'medium': return '#fbc02d';
    case 'low': return '#689f38';
    default: return '#757575';
  }
};

// Helper function to get priority icon
const getPriorityIcon = (priority?: string) => {
  if (priority === 'critical' || priority === 'high') {
    return <PriorityHighIcon fontSize="small" />;
  }
  return null;
};

// Helper function to format Firestore timestamp for display
const formatDueDate = (timestamp: any): string => {
  if (!timestamp) return 'No due date';
  
  // Handle Firestore timestamp objects
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toLocaleDateString();
  }
  
  // Handle serialized timestamps (seconds & nanoseconds)
  if (timestamp.seconds !== undefined) {
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  }
  
  // Handle Date objects or ISO strings
  try {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString();
    }
  } catch (e) {
    console.error('Error formatting date:', e);
  }
  
  return 'Invalid date';
}

// Custom theme for enhanced visual appeal
const customTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    secondary: {
      main: '#f50057',
      light: '#ff5983',
      dark: '#c51162',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
});

const NotepadTasks: React.FC = () => {
  const { user, userMetadata } = useContext(AuthContext);
  const [tabValue, setTabValue] = useState(0);
  const [notes, setNotes] = useState<Note[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [noteContent, setNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [newTask, setNewTask] = useState({ title: '', description: '', due_date: '', priority: 'medium', category: '' });
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [editTaskDialogOpen, setEditTaskDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [assignedTasks, setAssignedTasks] = useState<Task[]>([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Search and filter states
  const [noteSearch, setNoteSearch] = useState('');
  const [taskFilter, setTaskFilter] = useState<FilterState>({
    search: '',
    status: 'all',
    priority: 'all',
    assignee: 'all',
    dateRange: 'all',
    category: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [markdownPreview, setMarkdownPreview] = useState(false);
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);
  
  // Bulk operations state
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [bulkActionMode, setBulkActionMode] = useState(false);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);
  const [showShortcutsDialog, setShowShortcutsDialog] = useState(false);
  
  // Comments state
  const [selectedTaskForComments, setSelectedTaskForComments] = useState<Task | null>(null);
  const [taskComments, setTaskComments] = useState<TaskComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if user is typing in an input field
      const isInputField = 
        e.target instanceof HTMLInputElement || 
        e.target instanceof HTMLTextAreaElement || 
        (e.target as HTMLElement).contentEditable === 'true';
      
      if (isInputField) return;
      
      // Ctrl/Cmd + N: New task/note
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        if (tabValue === 0) {
          setNoteContent('');
          setEditingNoteId(null);
          setNoteDialogOpen(true);
        } else if (tabValue === 1) {
          setTaskDialogOpen(true);
        }
      }
      
      // Ctrl/Cmd + /: Focus search
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }
      
      // Escape: Close dialogs
      if (e.key === 'Escape') {
        setNoteDialogOpen(false);
        setTaskDialogOpen(false);
        setAssignDialogOpen(false);
        setBulkMenuAnchor(null);
      }
      
      // Tab navigation (Alt + 1-4)
      if (e.altKey && !isNaN(parseInt(e.key))) {
        const tabIndex = parseInt(e.key) - 1;
        if (tabIndex >= 0 && tabIndex <= 3) {
          e.preventDefault();
          setTabValue(tabIndex);
        }
      }
      
      // Ctrl/Cmd + E: Export (when on tasks or notes tab)
      if ((e.ctrlKey || e.metaKey) && e.key === 'e') {
        e.preventDefault();
        if (tabValue === 0 && filteredNotes.length > 0) {
          exportNotesToTxt();
        } else if (tabValue === 1 && filterTasks(tasks).length > 0) {
          exportTasksToCSV();
        }
      }
      
      // Ctrl/Cmd + S: Toggle select mode (tasks only)
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && tabValue === 1) {
        e.preventDefault();
        setBulkActionMode(!bulkActionMode);
        setSelectedTasks([]);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tabValue, bulkActionMode, notes, noteSearch, tasks, taskFilter]);

  // Filter notes based on search
  const filteredNotes = notes.filter(note => 
    note.content.toLowerCase().includes(noteSearch.toLowerCase())
  );

  // Filter tasks based on filters
  const filterTasks = (taskList: Task[]) => {
    return taskList.filter(task => {
      // Search filter
      if (taskFilter.search && 
          !task.title.toLowerCase().includes(taskFilter.search.toLowerCase()) &&
          !task.description.toLowerCase().includes(taskFilter.search.toLowerCase())) {
        return false;
      }

      // Status filter
      if (taskFilter.status !== 'all') {
        if (taskFilter.status === 'active' && (task.completed || task.archived)) return false;
        if (taskFilter.status === 'completed' && !task.completed) return false;
        if (taskFilter.status === 'overdue' && !isTaskOverdue(task)) return false;
      }

      // Priority filter
      if (taskFilter.priority !== 'all' && task.priority !== taskFilter.priority) {
        return false;
      }

      // Date range filter
      if (taskFilter.dateRange !== 'all' && task.due_date) {
        const taskDate = task.due_date.toDate ? task.due_date.toDate() : new Date(task.due_date);
        
        switch (taskFilter.dateRange) {
          case 'today':
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            if (taskDate < today || taskDate >= tomorrow) return false;
            break;
          case 'week':
            const weekFromNow = new Date();
            weekFromNow.setDate(weekFromNow.getDate() + 7);
            if (taskDate > weekFromNow) return false;
            break;
          case 'month':
            const monthFromNow = new Date();
            monthFromNow.setMonth(monthFromNow.getMonth() + 1);
            if (taskDate > monthFromNow) return false;
            break;
          case 'overdue':
            if (!isTaskOverdue(task)) return false;
            break;
        }
      }

      // Category filter
      if (taskFilter.category !== 'all' && task.category !== taskFilter.category) {
        return false;
      }

      return true;
    });
  };

  // Fetch notes and tasks from Firestore
  useEffect(() => {
    if (!user) return;
    
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch user's personal notes
        const notesRef = collection(db, 'notes');
        const notesQuery = query(notesRef, where('user_id', '==', user.uid));
        const notesSnapshot = await getDocs(notesQuery);
        const notesData = notesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Note));
        setNotes(notesData);

        // Fetch tasks (personal and assigned to the user)
        const tasksRef = collection(db, 'tasks');
        const tasksQuery = query(tasksRef, where('assigned_to', '==', user.uid));
        const tasksSnapshot = await getDocs(tasksQuery);
        const tasksData = tasksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Task));
        setTasks(tasksData);

        // If user is MANAGER or ADMIN, fetch available users for task assignment
        // and fetch tasks assigned by this user to others
        if (userMetadata?.role === 'MANAGER' || userMetadata?.role === 'ADMIN') {
          await fetchAvailableUsers();
          
          // Fetch tasks assigned by this user to others
          const assignedTasksQuery = query(tasksRef, where('assigned_by', '==', user.uid));
          const assignedTasksSnapshot = await getDocs(assignedTasksQuery);
          const assignedTasksData = assignedTasksSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as Task));
          
          // Filter out tasks that are assigned to the current user (already in tasks state)
          // and filter out tasks that the current user has hidden from their view
          const filteredAssignedTasks = assignedTasksData.filter(task => 
            task.assigned_to !== user.uid && 
            (!task.hidden_by || !task.hidden_by.includes(user.uid))
          );
          
          setAssignedTasks(filteredAssignedTasks);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user, userMetadata]);

  // Fetch users based on role permissions
  const fetchAvailableUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      let usersQuery;

      // Managers can only assign to users at their locations
      const locations = managedLocations(userMetadata);
      if (userMetadata?.role === 'MANAGER' && locations.length > 0) {
        usersQuery = query(usersRef, whereLocation('assigned_location', locations));
      } else {
        // Admins can assign to any user
        usersQuery = usersRef;
      }

      const usersSnapshot = await getDocs(usersQuery);
      const usersData = usersSnapshot.docs.map(doc => ({
        ...doc.data()
      } as User));
      setAvailableUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Note functions
  const saveNote = async () => {
    if (!user || !noteContent.trim()) return;

    try {
      if (editingNoteId) {
        // Update existing note
        const noteRef = doc(db, 'notes', editingNoteId);
        await updateDoc(noteRef, {
          content: noteContent,
          updated_at: serverTimestamp()
        });

        setNotes(prevNotes => prevNotes.map(note => 
          note.id === editingNoteId ? { ...note, content: noteContent, updated_at: new Date() } : note
        ));
        setEditingNoteId(null);
        setSuccessMessage(editingNoteId ? 'Note updated successfully' : 'Note saved successfully');
        setShowSuccess(true);
      } else {
        // Create new note
        const noteData = {
          content: noteContent,
          user_id: user.uid,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        };
        
        const docRef = await addDoc(collection(db, 'notes'), noteData);
        setNotes(prevNotes => [...prevNotes, { id: docRef.id, ...noteData, created_at: new Date(), updated_at: new Date() }]);
        setSuccessMessage('Note saved successfully');
        setShowSuccess(true);
      }
      
      setNoteContent('');
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  const editNote = (note: Note) => {
    setNoteContent(note.content);
    setEditingNoteId(note.id);
    setNoteDialogOpen(true);
  };

  const deleteNote = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'notes', id));
      setNotes(prevNotes => prevNotes.filter(note => note.id !== id));
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  // Task functions
  const saveTask = async () => {
    if (!user || !newTask.title.trim()) return;

    try {
      if (editingTask) {
        // Update existing task
        const taskRef = doc(db, 'tasks', editingTask.id);
        await updateDoc(taskRef, {
          title: newTask.title,
          description: newTask.description,
          due_date: newTask.due_date ? new Date(newTask.due_date) : null,
          priority: newTask.priority as 'critical' | 'high' | 'medium' | 'low',
          category: newTask.category || null,
          updated_at: serverTimestamp()
        });

        setTasks(prevTasks => prevTasks.map(task => 
          task.id === editingTask.id 
            ? { 
                ...task, 
                title: newTask.title,
                description: newTask.description,
                due_date: newTask.due_date ? new Date(newTask.due_date) : null,
                priority: newTask.priority as 'critical' | 'high' | 'medium' | 'low',
                category: newTask.category || undefined,
                updated_at: new Date()
              }
            : task
        ));
        
        setEditingTask(null);
        setEditTaskDialogOpen(false);
        setSuccessMessage('Task updated successfully');
        setShowSuccess(true);
      } else {
      const taskData: any = {
        title: newTask.title,
        description: newTask.description,
        completed: false,
        archived: false,
        assigned_to: user.uid, // Default to self
        assigned_by: user.uid,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        due_date: newTask.due_date ? new Date(newTask.due_date) : null,
        priority: (newTask.priority || 'medium') as 'critical' | 'high' | 'medium' | 'low'
      };
      
      // Only add optional fields if they have values
      if (newTask.category) {
        taskData.category = newTask.category;
      }
      
      const docRef = await addDoc(collection(db, 'tasks'), taskData);
      setTasks(prevTasks => [...prevTasks, { 
        id: docRef.id, 
        ...taskData, 
        created_at: new Date(), 
        updated_at: new Date() 
      }]);
      }
      
      setNewTask({ title: '', description: '', due_date: '', priority: 'medium', category: '' });
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  // Edit task function
  const startEditTask = (task: Task) => {
    setEditingTask(task);
    setNewTask({
      title: task.title,
      description: task.description,
      due_date: task.due_date ? 
        (task.due_date.toDate ? 
          task.due_date.toDate().toISOString().split('T')[0] : 
          new Date(task.due_date).toISOString().split('T')[0]
        ) : '',
      priority: task.priority || 'medium',
      category: task.category || ''
    });
    setEditTaskDialogOpen(true);
  };

  const toggleTaskStatus = async (task: Task) => {
    try {
      const taskRef = doc(db, 'tasks', task.id);
      await updateDoc(taskRef, {
        completed: !task.completed,
        updated_at: serverTimestamp()
      });

      setTasks(prevTasks => prevTasks.map(t => 
        t.id === task.id ? { ...t, completed: !t.completed, updated_at: new Date() } : t
      ));
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const archiveTask = async (task: Task) => {
    try {
      const taskRef = doc(db, 'tasks', task.id);
      await updateDoc(taskRef, {
        archived: true,
        updated_at: serverTimestamp()
      });

      setTasks(prevTasks => prevTasks.map(t => 
        t.id === task.id ? { ...t, archived: true, updated_at: new Date() } : t
      ));
    } catch (error) {
      console.error('Error archiving task:', error);
    }
  };

  const clearFilters = () => {
    setTaskFilter({
      search: '',
      status: 'all',
      priority: 'all',
      assignee: 'all',
      dateRange: 'all',
      category: 'all'
    });
  };

  // Bulk operations functions
  const handleSelectTask = (taskId: string) => {
    setSelectedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  };

  const handleSelectAllTasks = () => {
    const visibleTasks = filterTasks(tasks).filter(task => !task.completed && !task.archived);
    if (selectedTasks.length === visibleTasks.length) {
      setSelectedTasks([]);
    } else {
      setSelectedTasks(visibleTasks.map(task => task.id));
    }
  };

  const handleBulkComplete = async () => {
    try {
      const batch = selectedTasks.map(taskId => {
        const taskRef = doc(db, 'tasks', taskId);
        return updateDoc(taskRef, {
          completed: true,
          updated_at: serverTimestamp()
        });
      });
      
      await Promise.all(batch);
      
      setTasks(prevTasks => 
        prevTasks.map(task => 
          selectedTasks.includes(task.id) 
            ? { ...task, completed: true, updated_at: new Date() }
            : task
        )
      );
      
      setSelectedTasks([]);
      setBulkActionMode(false);
      setSuccessMessage(`${selectedTasks.length} tasks marked as complete`);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error completing tasks:', error);
    }
  };

  const handleBulkArchive = async () => {
    try {
      const batch = selectedTasks.map(taskId => {
        const taskRef = doc(db, 'tasks', taskId);
        return updateDoc(taskRef, {
          archived: true,
          updated_at: serverTimestamp()
        });
      });
      
      await Promise.all(batch);
      
      setTasks(prevTasks => 
        prevTasks.map(task => 
          selectedTasks.includes(task.id) 
            ? { ...task, archived: true, updated_at: new Date() }
            : task
        )
      );
      
      setSelectedTasks([]);
      setBulkActionMode(false);
      setSuccessMessage(`${selectedTasks.length} tasks archived`);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error archiving tasks:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedTasks.length} tasks? This cannot be undone.`)) {
      return;
    }
    
    try {
      const batch = selectedTasks.map(taskId => {
        const taskRef = doc(db, 'tasks', taskId);
        return deleteDoc(taskRef);
      });
      
      await Promise.all(batch);
      
      setTasks(prevTasks => 
        prevTasks.filter(task => !selectedTasks.includes(task.id))
      );
      
      setSelectedTasks([]);
      setBulkActionMode(false);
      setSuccessMessage(`${selectedTasks.length} tasks deleted`);
      setShowSuccess(true);
    } catch (error) {
      console.error('Error deleting tasks:', error);
    }
  };

  // Export functions
  const exportTasksToCSV = () => {
    const tasksToExport = filterTasks(tasks);
    if (tasksToExport.length === 0) {
      alert('No tasks to export');
      return;
    }
    
    // CSV headers
    const headers = ['Title', 'Description', 'Status', 'Priority', 'Category', 'Due Date', 'Created Date', 'Assigned By'];
    
    // Convert tasks to CSV rows
    const rows = tasksToExport.map(task => [
      task.title,
      task.description || '',
      task.completed ? 'Completed' : task.archived ? 'Archived' : 'Active',
      task.priority || 'medium',
      task.category ? TASK_CATEGORIES.find(c => c.id === task.category)?.label || '' : '',
      task.due_date ? formatDueDate(task.due_date) : '',
      task.created_at ? new Date(task.created_at.seconds ? task.created_at.seconds * 1000 : task.created_at).toLocaleDateString() : '',
      task.assigned_by === user?.uid ? 'Me' : 'Assigned to me'
    ]);
    
    // Convert to CSV string
    const csvContent = [
      headers.map(h => `"${h}"`).join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `tasks_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSuccessMessage('Tasks exported successfully');
    setShowSuccess(true);
  };

  const exportNotesToTxt = () => {
    const notesToExport = filteredNotes;
    if (notesToExport.length === 0) {
      alert('No notes to export');
      return;
    }
    
    // Convert notes to text format
    const textContent = notesToExport
      .map(note => {
        const date = note.updated_at?.toDate ? note.updated_at.toDate().toLocaleString() : 'Unknown date';
        return `${'='.repeat(60)}\nNote updated: ${date}\n${'='.repeat(60)}\n\n${note.content}\n\n`;
      })
      .join('\n');
    
    // Create blob and download
    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `notes_export_${new Date().toISOString().split('T')[0]}.txt`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSuccessMessage('Notes exported successfully');
    setShowSuccess(true);
  };

  // Comment functions
  const openCommentsDialog = (task: Task) => {
    setSelectedTaskForComments(task);
    setCommentsDialogOpen(true);
    loadTaskComments(task.id);
  };

  const loadTaskComments = async (taskId: string) => {
    setLoadingComments(true);
    try {
      const commentsRef = collection(db, 'tasks', taskId, 'comments');
      const commentsQuery = query(commentsRef, orderBy('created_at', 'desc'));
      
      // Set up real-time listener
      const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
        const comments = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as TaskComment));
        setTaskComments(comments);
        setLoadingComments(false);
      });

      // Store unsubscribe function to clean up when dialog closes
      return unsubscribe;
    } catch (error) {
      console.error('Error loading comments:', error);
      setLoadingComments(false);
    }
  };

  const addComment = async () => {
    if (!user || !newComment.trim() || !selectedTaskForComments) return;

    try {
      const commentData = {
        task_id: selectedTaskForComments.id,
        user_id: user.uid,
        user_name: userMetadata?.displayName || user.email || 'Unknown User',
        content: newComment.trim(),
        created_at: serverTimestamp(),
        edited: false
      };

      // Add comment to subcollection
      await addDoc(collection(db, 'tasks', selectedTaskForComments.id, 'comments'), commentData);
      
      // Update comment count on task
      const taskRef = doc(db, 'tasks', selectedTaskForComments.id);
      await updateDoc(taskRef, {
        comment_count: increment(1),
        updated_at: serverTimestamp()
      });

      // Update local task state
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === selectedTaskForComments.id 
            ? { ...task, comment_count: (task.comment_count || 0) + 1 }
            : task
        )
      );

      setNewComment('');
      setSuccessMessage('Comment added');
      setShowSuccess(true);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Close comments dialog and cleanup listener
  const closeCommentsDialog = () => {
    setCommentsDialogOpen(false);
    setSelectedTaskForComments(null);
    setTaskComments([]);
    setNewComment('');
  };

  const handleAssignTask = async () => {
    if (!selectedUserId || !newTask.title.trim()) return;

    try {
      const taskData: Omit<Task, 'id'> = {
        title: newTask.title,
        description: newTask.description,
        completed: false,
        archived: false,
        assigned_to: selectedUserId,
        assigned_by: user?.uid || '',
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        due_date: newTask.due_date ? new Date(newTask.due_date) : null,
        hidden_by: [],
        priority: (newTask.priority || 'medium') as 'critical' | 'high' | 'medium' | 'low'
      };
      
      // Only add optional fields if they have values
      if (newTask.category) {
        taskData.category = newTask.category;
      }
      
      // Add the task to Firestore
      const docRef = await addDoc(collection(db, 'tasks'), taskData);
      
      // Add the new task to the assignedTasks state
      const assignedTask: Task = {
        id: docRef.id,
        ...taskData,
        created_at: new Date(),
        updated_at: new Date(),
        hidden_by: []
      };
      setAssignedTasks(prev => [...prev, assignedTask]);
      
      // Reset form and show success message
      setNewTask({ title: '', description: '', due_date: '', priority: 'medium', category: '' });
      setSelectedUserId('');
      setAssignDialogOpen(false);
      setSuccessMessage(`Task successfully assigned to ${availableUsers.find(u => u.uid === selectedUserId)?.displayName || 'user'}`);
      setShowSuccess(true);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error assigning task:', error);
    }
  };
  
  // Hide a task from the manager/admin view (doesn't delete it)
  const hideAssignedTask = async (taskId: string) => {
    if (!user) return;
    
    try {
      const taskRef = doc(db, 'tasks', taskId);
      const taskSnap = await getDoc(taskRef);
      
      if (taskSnap.exists()) {
        const taskData = taskSnap.data() as Task;
        const hiddenBy = taskData.hidden_by || [];
        
        // Add current user to hidden_by array if not already there
        if (!hiddenBy.includes(user.uid)) {
          await updateDoc(taskRef, {
            hidden_by: [...hiddenBy, user.uid],
            updated_at: serverTimestamp()
          });
          
          // Update local state
          setAssignedTasks(prev => prev.filter(task => task.id !== taskId));
        }
      }
    } catch (error) {
      console.error('Error hiding task:', error);
    }
  };

  // Render UI
  if (isLoading) {
    return (
      <Container>
        <LoadingSpinner minHeight="80vh" />
      </Container>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: 4, 
          mb: 4,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          minHeight: '100vh',
          borderRadius: 4,
          p: 3
        }}
      >
      {/* Success notification */}
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={3000} 
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 100,
            height: 100,
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '50%',
          }
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Notepad & Tasks
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Organize your thoughts and manage your tasks efficiently
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Keyboard Shortcuts">
              <IconButton 
                onClick={() => setShowShortcutsDialog(true)}
                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)'
                  }
                }}
              >
                <KeyboardIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Paper 
        elevation={0} 
        sx={{ 
          mb: 3,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="notepad and tasks tabs"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ 
            minHeight: 56,
            '& .MuiTabs-flexContainer': {
              justifyContent: { xs: 'flex-start', md: 'flex-start' }
            },
            '& .MuiTab-root': {
              minHeight: 56,
              minWidth: { xs: 'auto', sm: 160 },
              px: { xs: 2, sm: 3 },
              fontSize: '0.95rem',
              fontWeight: 500,
              transition: 'all 0.3s',
              '&:hover': {
                backgroundColor: alpha(customTheme.palette.primary.main, 0.08),
              },
              '&.Mui-selected': {
                fontWeight: 600,
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
            }
          }}
        >
          <Tab label="Personal Notepad" />
          <Tab label="My Tasks" />
          {(userMetadata?.role === 'MANAGER' || userMetadata?.role === 'ADMIN') && (
            <Tab label="Assign Tasks" />
          )}
          {(userMetadata?.role === 'MANAGER' || userMetadata?.role === 'ADMIN') && (
            <Tab 
              label={
                <Badge 
                  badgeContent={assignedTasks.length} 
                  color="primary"
                  max={99}
                  showZero={false}
                >
                  <Box sx={{ pr: 1 }}>Assigned Tasks</Box>
                </Badge>
              } 
            />
          )}
        </Tabs>
      </Paper>

      {/* Personal Notepad Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <TextField
                placeholder="Search notes..."
                variant="outlined"
                size="small"
                value={noteSearch}
                onChange={(e) => setNoteSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: noteSearch && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setNoteSearch('')}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: '100%', sm: 400 } }}
              />
              <Box display="flex" gap={1}>
                {filteredNotes.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportNotesToTxt}
                  >
                    Export
                  </Button>
                )}
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setNoteContent('');
                    setEditingNoteId(null);
                    setNoteDialogOpen(true);
                  }}
                >
                  Add New Note
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              My Notes {noteSearch && `(${filteredNotes.length} results)`}
            </Typography>
            {filteredNotes.length > 0 ? (
              filteredNotes.map((note) => (
                <Paper key={note.id} elevation={1} sx={{ p: 2, mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box sx={{ flexGrow: 1 }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {note.content}
                      </ReactMarkdown>
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => editNote(note)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => deleteNote(note.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    Last updated: {note.updated_at?.toDate ? note.updated_at.toDate().toLocaleString() : 'Just now'}
                  </Typography>
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                {noteSearch ? 'No notes found matching your search.' : 'No notes yet. Start by creating one above.'}
              </Typography>
            )}
          </Grid>
        </Grid>
      )}

      {/* My Tasks Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {/* Search and Filters */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
              <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                <TextField
                  placeholder="Search tasks..."
                  variant="outlined"
                  size="small"
                  value={taskFilter.search}
                  onChange={(e) => setTaskFilter({...taskFilter, search: e.target.value})}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: taskFilter.search && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setTaskFilter({...taskFilter, search: ''})}
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: { xs: '100%', sm: 300 } }}
                />
                <Button
                  variant="outlined"
                  startIcon={showFilters ? <ExpandLessIcon /> : <FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  size="small"
                >
                  Filters
                </Button>
                {(taskFilter.status !== 'all' || taskFilter.priority !== 'all' || taskFilter.dateRange !== 'all' || taskFilter.category !== 'all') && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={clearFilters}
                    startIcon={<ClearIcon />}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
              <Box display="flex" gap={1}>
                {bulkActionMode && selectedTasks.length > 0 && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => setBulkMenuAnchor(e.currentTarget)}
                      startIcon={<MoreVertIcon />}
                    >
                      Actions ({selectedTasks.length})
                    </Button>
                    <Menu
                      anchorEl={bulkMenuAnchor}
                      open={Boolean(bulkMenuAnchor)}
                      onClose={() => setBulkMenuAnchor(null)}
                    >
                      <MenuItem onClick={() => { handleBulkComplete(); setBulkMenuAnchor(null); }}>
                        <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Mark as Complete</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => { handleBulkArchive(); setBulkMenuAnchor(null); }}>
                        <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Archive</ListItemText>
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={() => { handleBulkDelete(); setBulkMenuAnchor(null); }}>
                        <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
                <Button
                  variant={bulkActionMode ? "outlined" : "text"}
                  size="small"
                  onClick={() => {
                    setBulkActionMode(!bulkActionMode);
                    setSelectedTasks([]);
                  }}
                  startIcon={<CheckBoxIcon />}
                >
                  {bulkActionMode ? 'Cancel' : 'Select'}
                </Button>
                {filterTasks(tasks).length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportTasksToCSV}
                  >
                    Export
                  </Button>
                )}
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={() => setTaskDialogOpen(true)}
                >
                  Add Task
                </Button>
              </Box>
            </Box>

            {/* Filter Panel */}
            {showFilters && (
              <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={taskFilter.status}
                        onChange={(e) => setTaskFilter({...taskFilter, status: e.target.value as any})}
                        label="Status"
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="overdue">Overdue</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={taskFilter.priority}
                        onChange={(e) => setTaskFilter({...taskFilter, priority: e.target.value as any})}
                        label="Priority"
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="critical">Critical</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="low">Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Date Range</InputLabel>
                      <Select
                        value={taskFilter.dateRange}
                        onChange={(e) => setTaskFilter({...taskFilter, dateRange: e.target.value as any})}
                        label="Date Range"
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="today">Today</MenuItem>
                        <MenuItem value="week">This Week</MenuItem>
                        <MenuItem value="month">This Month</MenuItem>
                        <MenuItem value="overdue">Overdue</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={taskFilter.category}
                        onChange={(e) => setTaskFilter({...taskFilter, category: e.target.value})}
                        label="Category"
                      >
                        <MenuItem value="all">All</MenuItem>
                        {TASK_CATEGORIES.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: cat.color,
                                }}
                              />
                              {cat.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Active Tasks ({filterTasks(tasks).filter(task => !task.completed && !task.archived).length})
                </Typography>
                {bulkActionMode && filterTasks(tasks).filter(task => !task.completed && !task.archived).length > 0 && (
                  <Button
                    size="small"
                    onClick={handleSelectAllTasks}
                    startIcon={<CheckBoxIcon />}
                  >
                    {selectedTasks.length === filterTasks(tasks).filter(task => !task.completed && !task.archived).length 
                      ? 'Deselect All' 
                      : 'Select All'}
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filterTasks(tasks).filter(task => !task.completed && !task.archived).length > 0 ? (
                  filterTasks(tasks)
                    .filter(task => !task.completed && !task.archived)
                    .sort((a, b) => {
                      // Sort by due date (ascending)
                      if (!a.due_date) return 1; // Tasks without due dates go last
                      if (!b.due_date) return -1;
                      
                      // Get timestamp in milliseconds for comparison
                      const getTimestamp = (task: Task): number => {
                        if (task.due_date?.toDate && typeof task.due_date.toDate === 'function') {
                          return task.due_date.toDate().getTime();
                        }
                        if (task.due_date?.seconds !== undefined) {
                          return task.due_date.seconds * 1000;
                        }
                        if (task.due_date) {
                          const date = new Date(task.due_date);
                          if (!isNaN(date.getTime())) {
                            return date.getTime();
                          }
                        }
                        return Number.MAX_SAFE_INTEGER; // Fallback for invalid dates
                      };
                      
                      return getTimestamp(a) - getTimestamp(b);
                    })
                    .map((task) => (
                      <Card 
                        key={task.id}
                        elevation={isTaskOverdue(task) ? 3 : 1}
                        sx={{ 
                          borderLeft: `4px solid ${isTaskOverdue(task) ? '#d32f2f' : getPriorityColor(task.priority)}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          background: 'linear-gradient(to right, rgba(255,255,255,0.9), rgba(255,255,255,1))',
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            transform: 'translateY(-4px) scale(1.01)',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                          }
                        }}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            {bulkActionMode && (
                              <Checkbox
                                checked={selectedTasks.includes(task.id)}
                                onChange={() => handleSelectTask(task.id)}
                                size="small"
                                sx={{ mt: -1, ml: -1, mr: 1 }}
                              />
                            )}
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Typography 
                                  variant="h6" 
                                  style={{ 
                                    fontWeight: task.priority === 'critical' || task.priority === 'high' ? 600 : 400,
                                    fontSize: '1.1rem'
                                  }}
                                >
                                  {task.title}
                                </Typography>
                                {getPriorityIcon(task.priority)}
                                {isTaskOverdue(task) && (
                                  <Tooltip title="Overdue">
                                    <WarningIcon fontSize="small" color="error" />
                                  </Tooltip>
                                )}
                              </Box>
                              {task.description && (
                                <Typography variant="body2" color="text.secondary" paragraph>
                                  {task.description}
                                </Typography>
                              )}
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {task.due_date && (
                                  <Chip
                                    size="small"
                                    icon={<ScheduleIcon />}
                                    label={formatDueDate(task.due_date)}
                                    color={isTaskOverdue(task) ? "error" : "default"}
                                    variant="outlined"
                                  />
                                )}
                                {task.priority && (
                                  <Chip
                                    size="small"
                                    label={task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                                    style={{
                                      backgroundColor: getPriorityColor(task.priority) + '20',
                                      color: getPriorityColor(task.priority),
                                      borderColor: getPriorityColor(task.priority)
                                    }}
                                    variant="outlined"
                                  />
                                )}
                                {task.assigned_by !== user?.uid && (
                                  <Chip 
                                    icon={<PersonIcon />} 
                                    label="Assigned to you" 
                                    size="small" 
                                    color="secondary"
                                  />
                                )}
                              </Box>
                              {/* Category */}
                              {task.category && (
                                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                                  <Chip
                                    size="small"
                                    icon={<LabelIcon />}
                                    label={TASK_CATEGORIES.find(c => c.id === task.category)?.label}
                                    style={{
                                      backgroundColor: TASK_CATEGORIES.find(c => c.id === task.category)?.color + '20',
                                      color: TASK_CATEGORIES.find(c => c.id === task.category)?.color,
                                      borderColor: TASK_CATEGORIES.find(c => c.id === task.category)?.color
                                    }}
                                    variant="outlined"
                                  />
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                        <Divider />
                        <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title="Comments">
                              <IconButton 
                                onClick={() => openCommentsDialog(task)}
                                size="small"
                                sx={{ 
                                  color: task.comment_count ? 'primary.main' : 'text.secondary',
                                  '&:hover': {
                                    backgroundColor: alpha(customTheme.palette.primary.main, 0.08)
                                  }
                                }}
                              >
                                <Badge badgeContent={task.comment_count || 0} color="primary">
                                  <CommentIcon fontSize="small" />
                                </Badge>
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box display="flex" gap={0.5}>
                            {task.assigned_by === user?.uid && (
                              <Tooltip title="Edit task">
                                <IconButton 
                                  onClick={() => {
                                    startEditTask(task);
                                    setTaskDialogOpen(true);
                                  }}
                                  color="primary"
                                  size="small"
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: alpha(customTheme.palette.primary.main, 0.08),
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Mark as complete">
                              <IconButton 
                                onClick={() => toggleTaskStatus(task)}
                                color="primary"
                                size="small"
                                sx={{
                                  '&:hover': {
                                    backgroundColor: alpha(customTheme.palette.primary.main, 0.08),
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <CheckCircleOutlineIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Archive">
                              <IconButton 
                                onClick={() => archiveTask(task)}
                                color="secondary"
                                size="small"
                                sx={{
                                  '&:hover': {
                                    backgroundColor: alpha(customTheme.palette.secondary.main, 0.08),
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <ArchiveIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardActions>
                      </Card>
                    ))
                ) : (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.50' }}>
                    <Typography variant="body1" color="textSecondary">
                      {taskFilter.search || taskFilter.status !== 'all' || taskFilter.priority !== 'all' || taskFilter.dateRange !== 'all' 
                        ? 'No tasks match your filters.' 
                        : 'No active tasks. Create one to get started!'}
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Completed Tasks ({filterTasks(tasks).filter(task => task.completed && !task.archived).length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filterTasks(tasks).filter(task => task.completed && !task.archived).length > 0 ? (
                  filterTasks(tasks)
                    .filter(task => task.completed && !task.archived)
                    .map((task) => (
                      <Card 
                        key={task.id}
                        elevation={1}
                        sx={{ 
                          borderLeft: '4px solid #4caf50',
                          opacity: 0.8,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            opacity: 1,
                            transform: 'translateY(-2px)',
                            boxShadow: 2
                          }
                        }}
                      >
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            {bulkActionMode && (
                              <Checkbox
                                checked={selectedTasks.includes(task.id)}
                                onChange={() => handleSelectTask(task.id)}
                                size="small"
                                sx={{ mt: -1, ml: -1, mr: 1 }}
                              />
                            )}
                            <Box flex={1}>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <CheckCircleIcon color="success" fontSize="small" />
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    textDecoration: 'line-through',
                                    fontSize: '1.1rem',
                                    color: 'text.secondary'
                                  }}
                                >
                                  {task.title}
                                </Typography>
                              </Box>
                              {task.description && (
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary" 
                                  paragraph
                                  sx={{ textDecoration: 'line-through' }}
                                >
                                  {task.description}
                                </Typography>
                              )}
                              <Box display="flex" gap={1} flexWrap="wrap">
                                {task.due_date && (
                                  <Chip
                                    size="small"
                                    icon={<ScheduleIcon />}
                                    label={`Was due: ${formatDueDate(task.due_date)}`}
                                    variant="outlined"
                                    sx={{ opacity: 0.7 }}
                                  />
                                )}
                                <Chip
                                  size="small"
                                  icon={<CheckCircleIcon />}
                                  label="Completed"
                                  color="success"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                        <Divider />
                        <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title="Comments">
                              <IconButton 
                                onClick={() => openCommentsDialog(task)}
                                size="small"
                                sx={{ 
                                  color: task.comment_count ? 'primary.main' : 'text.secondary',
                                  '&:hover': {
                                    backgroundColor: alpha(customTheme.palette.primary.main, 0.08)
                                  }
                                }}
                              >
                                <Badge badgeContent={task.comment_count || 0} color="primary">
                                  <CommentIcon fontSize="small" />
                                </Badge>
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Tooltip title="Archive">
                            <IconButton 
                              onClick={() => archiveTask(task)}
                              color="secondary"
                              size="small"
                              sx={{
                                '&:hover': {
                                  backgroundColor: alpha(customTheme.palette.secondary.main, 0.08),
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <ArchiveIcon />
                            </IconButton>
                          </Tooltip>
                        </CardActions>
                      </Card>
                    ))
                ) : (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.50' }}>
                    <Typography variant="body1" color="textSecondary">
                      No completed tasks yet.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Assigned Tasks Tab - Shows tasks assigned to others */}
      {tabValue === 3 && userMetadata && (userMetadata.role === 'MANAGER' || userMetadata.role === 'ADMIN') && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tasks You've Assigned
              </Typography>
              
              {assignedTasks.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  You haven't assigned any tasks yet, or all assigned tasks have been hidden.
                </Typography>
              ) : (
                <List>
                  {/* Active Tasks */}
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                    Active Tasks
                  </Typography>
                  {assignedTasks.filter(task => !task.completed && !task.archived).length > 0 ? (
                    assignedTasks
                      .filter(task => !task.completed && !task.archived)
                      .sort((a, b) => {
                        // Sort by due date (ascending)
                        if (!a.due_date) return 1;
                        if (!b.due_date) return -1;
                        
                        // Get timestamp in milliseconds for comparison
                        const getTimestamp = (task: Task): number => {
                          if (task.due_date?.toDate && typeof task.due_date.toDate === 'function') {
                            return task.due_date.toDate().getTime();
                          }
                          if (task.due_date?.seconds !== undefined) {
                            return task.due_date.seconds * 1000;
                          }
                          if (task.due_date) {
                            const date = new Date(task.due_date);
                            if (!isNaN(date.getTime())) {
                              return date.getTime();
                            }
                          }
                          return Number.MAX_SAFE_INTEGER;
                        };
                        
                        return getTimestamp(a) - getTimestamp(b);
                      })
                      .map((task) => (
                        <ListItem 
                          key={task.id}
                          secondaryAction={
                            <Box display="flex" gap={1}>
                              <Tooltip title="Comments">
                                <IconButton 
                                  onClick={() => openCommentsDialog(task)}
                                  size="small"
                                  sx={{ 
                                    color: task.comment_count ? 'primary.main' : 'text.secondary',
                                  }}
                                >
                                  <Badge badgeContent={task.comment_count || 0} color="primary">
                                    <CommentIcon fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                              <IconButton 
                                edge="end" 
                                onClick={() => hideAssignedTask(task.id)}
                                color="default"
                                title="Hide from your view"
                              >
                                <VisibilityOffIcon />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemIcon>
                            <AssignmentIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography>
                                {task.title}
                                <Chip 
                                  size="small" 
                                  label="Active" 
                                  color="primary" 
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                />
                              </Typography>
                            }
                            secondary={
                              <React.Fragment>
                                <Typography variant="body2" component="span" display="block">
                                  {task.description}
                                </Typography>
                                {task.due_date && (
                                  <Typography variant="caption" color="textSecondary" component="span" display="block">
                                    Due: {formatDueDate(task.due_date)}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="textSecondary" component="span" display="block">
                                  Assigned to: {availableUsers.find(u => u.uid === task.assigned_to)?.displayName || 'Unknown User'}
                                </Typography>
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                      ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No active assigned tasks.
                    </Typography>
                  )}
                  
                  {/* Completed Tasks */}
                  <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                    Completed Tasks
                  </Typography>
                  {assignedTasks.filter(task => task.completed && !task.archived).length > 0 ? (
                    assignedTasks
                      .filter(task => task.completed && !task.archived)
                      .map((task) => (
                        <ListItem 
                          key={task.id}
                          secondaryAction={
                            <Box display="flex" gap={1}>
                              <Tooltip title="Comments">
                                <IconButton 
                                  onClick={() => openCommentsDialog(task)}
                                  size="small"
                                  sx={{ 
                                    color: task.comment_count ? 'primary.main' : 'text.secondary',
                                  }}
                                >
                                  <Badge badgeContent={task.comment_count || 0} color="primary">
                                    <CommentIcon fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                              <IconButton 
                                edge="end" 
                                onClick={() => hideAssignedTask(task.id)}
                                color="default"
                                title="Hide from your view"
                              >
                                <VisibilityOffIcon />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemIcon>
                            <CheckCircleIcon color="success" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography sx={{ textDecoration: 'line-through' }}>
                                {task.title}
                                <Chip 
                                  size="small" 
                                  label="Completed" 
                                  color="success" 
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                />
                              </Typography>
                            }
                            secondary={
                              <React.Fragment>
                                <Typography variant="body2" component="span" display="block" sx={{ textDecoration: 'line-through' }}>
                                  {task.description}
                                </Typography>
                                {task.due_date && (
                                  <Typography variant="caption" color="textSecondary" component="span" display="block">
                                    Due: {formatDueDate(task.due_date)}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="textSecondary" component="span" display="block">
                                  Completed by: {availableUsers.find(u => u.uid === task.assigned_to)?.displayName || 'Unknown User'}
                                </Typography>
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                      ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No completed assigned tasks.
                    </Typography>
                  )}
                  
                  {/* Archived Tasks */}
                  <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                    Archived Tasks
                  </Typography>
                  {assignedTasks.filter(task => task.archived).length > 0 ? (
                    assignedTasks
                      .filter(task => task.archived)
                      .map((task) => (
                        <ListItem 
                          key={task.id}
                          secondaryAction={
                            <Box display="flex" gap={1}>
                              <Tooltip title="Comments">
                                <IconButton 
                                  onClick={() => openCommentsDialog(task)}
                                  size="small"
                                  sx={{ 
                                    color: task.comment_count ? 'primary.main' : 'text.secondary',
                                  }}
                                >
                                  <Badge badgeContent={task.comment_count || 0} color="primary">
                                    <CommentIcon fontSize="small" />
                                  </Badge>
                                </IconButton>
                              </Tooltip>
                              <IconButton 
                                edge="end" 
                                onClick={() => hideAssignedTask(task.id)}
                                color="default"
                                title="Hide from your view"
                              >
                                <VisibilityOffIcon />
                              </IconButton>
                            </Box>
                          }
                        >
                          <ListItemIcon>
                            <ArchiveIcon color="action" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={
                              <Typography sx={{ color: 'text.secondary' }}>
                                {task.title}
                                <Chip 
                                  size="small" 
                                  label="Archived" 
                                  color="default" 
                                  variant="outlined"
                                  sx={{ ml: 1 }}
                                />
                              </Typography>
                            }
                            secondary={
                              <React.Fragment>
                                <Typography variant="body2" component="span" display="block" sx={{ color: 'text.secondary' }}>
                                  {task.description}
                                </Typography>
                                {task.due_date && (
                                  <Typography variant="caption" color="textSecondary" component="span" display="block">
                                    Due: {formatDueDate(task.due_date)}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="textSecondary" component="span" display="block">
                                  Assigned to: {availableUsers.find(u => u.uid === task.assigned_to)?.displayName || 'Unknown User'}
                                </Typography>
                              </React.Fragment>
                            }
                          />
                        </ListItem>
                      ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No archived assigned tasks.
                    </Typography>
                  )}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>
      )} 

      {/* Add Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => {
        setTaskDialogOpen(false);
        setEditingTask(null);
        setNewTask({ title: '', description: '', due_date: '', priority: 'medium', category: '' });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'Add New Task'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Task Title"
            variant="outlined"
            value={newTask.title}
            onChange={(e) => setNewTask({...newTask, title: e.target.value})}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            variant="outlined"
            value={newTask.description}
            onChange={(e) => setNewTask({...newTask, description: e.target.value})}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                variant="outlined"
                InputLabelProps={{ shrink: true } as any}
                value={newTask.due_date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTask({...newTask, due_date: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                  label="Priority"
                >
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Category</InputLabel>
                <Select
                  value={newTask.category}
                  onChange={(e) => setNewTask({...newTask, category: e.target.value})}
                  label="Category"
                >
                  <MenuItem value="">None</MenuItem>
                  {TASK_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: cat.color,
                          }}
                        />
                        {cat.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setTaskDialogOpen(false);
            setNewTask({ title: '', description: '', due_date: '', priority: 'medium', category: '' });
          }} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              saveTask();
              setTaskDialogOpen(false);
            }} 
            color="primary" 
            variant="contained"
            disabled={!newTask.title.trim()}
          >
            {editingTask ? 'Update Task' : 'Add Task'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{editingNoteId ? 'Edit Note' : 'Add New Note'}</Typography>
            <ToggleButtonGroup
              value={markdownPreview ? 'preview' : 'edit'}
              exclusive
              onChange={(_, value) => setMarkdownPreview(value === 'preview')}
              size="small"
            >
              <ToggleButton value="edit">Edit</ToggleButton>
              <ToggleButton value="preview">Preview</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!markdownPreview ? (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" color="text.secondary">
                  Supports Markdown formatting
                </Typography>
                <Button
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => setShowMarkdownHelp(!showMarkdownHelp)}
                >
                  {showMarkdownHelp ? 'Hide' : 'Show'} Markdown Help
                </Button>
              </Box>
              <Collapse in={showMarkdownHelp}>
                <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" gutterBottom>Text Formatting</Typography>
                      <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
{`**bold text**
*italic text*
~~strikethrough~~
\`inline code\`
[link text](url)`}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" gutterBottom>Lists & More</Typography>
                      <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace' }}>
{`# Heading 1
## Heading 2
- Bullet list
1. Numbered list
> Blockquote
\`\`\`
Code block
\`\`\``}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Collapse>
              <TextField
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                placeholder="Write your note here... (Markdown supported)"
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                autoFocus
              />
            </Box>
          ) : (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                minHeight: 300,
                backgroundColor: 'grey.50',
                '& h1, & h2, & h3': { mt: 2, mb: 1 },
                '& p': { mb: 1 },
                '& ul, & ol': { pl: 3 },
                '& code': { 
                  backgroundColor: 'grey.200',
                  padding: '2px 4px',
                  borderRadius: 1,
                  fontSize: '0.9em'
                },
                '& pre': {
                  backgroundColor: 'grey.900',
                  color: 'white',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto'
                },
                '& blockquote': {
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  my: 2
                }
              }}
            >
              {noteContent ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {noteContent}
                </ReactMarkdown>
              ) : (
                <Typography color="text.secondary">Nothing to preview yet...</Typography>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNoteDialogOpen(false);
            setNoteContent('');
            setEditingNoteId(null);
          }} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={() => {
              saveNote();
              setNoteDialogOpen(false);
            }} 
            color="primary"
            disabled={!noteContent.trim()}
            startIcon={<SaveIcon />}
          >
            {editingNoteId ? 'Update Note' : 'Save Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Task Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Task</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="assign-user-label">Assign To</InputLabel>
            <Select
              labelId="assign-user-label"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value as string)}
              label="Assign To"
            >
              {availableUsers.map((user) => (
                <MenuItem key={user.uid} value={user.uid}>
                  {user.displayName} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Due Date"
            type="date"
            variant="outlined"
            InputLabelProps={{ shrink: true } as any}
            value={newTask.due_date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTask({...newTask, due_date: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleAssignTask} 
            color="primary" 
            variant="contained"
            disabled={!selectedUserId || !newTask.title.trim()}
          >
            Assign Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Tasks Tab (ADMIN/MANAGER only) */}
      {tabValue === 2 && (userMetadata?.role === 'MANAGER' || userMetadata?.role === 'ADMIN') && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Assign Task to Team Member
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Task Title"
                    variant="outlined"
                    value={newTask.title}
                    onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    variant="outlined"
                    value={newTask.description}
                    onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Due Date"
                    type="date"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    value={newTask.due_date}
                    onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Assign To</InputLabel>
                    <Select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value as string)}
                      label="Assign To"
                    >
                      {availableUsers.map((user) => (
                        <MenuItem key={user.uid} value={user.uid}>
                          {user.displayName} ({user.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<PersonIcon />}
                      onClick={handleAssignTask}
                      disabled={!newTask.title.trim() || !selectedUserId}
                    >
                      Assign Task
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Comments Dialog */}
      <Dialog 
        open={commentsDialogOpen} 
        onClose={closeCommentsDialog} 
        maxWidth="md" 
        fullWidth
        TransitionComponent={Fade}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,1))',
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="h6">Task Comments</Typography>
              {selectedTaskForComments && (
                <Chip 
                  label={selectedTaskForComments.title} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
              )}
            </Box>
            <IconButton onClick={closeCommentsDialog} size="small">
              <ClearIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ minHeight: 400, maxHeight: 600, display: 'flex', flexDirection: 'column' }}>
          {loadingComments ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <LoadingSpinner />
            </Box>
          ) : (
            <>
              {/* Comments List */}
              <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                {taskComments.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <CommentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography color="text.secondary">
                      No comments yet. Be the first to add one!
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {taskComments.map((comment, index) => (
                      <Fade in={true} timeout={300 * (index + 1)} key={comment.id}>
                        <ListItem 
                          alignItems="flex-start"
                          sx={{
                            backgroundColor: index % 2 === 0 ? 'grey.50' : 'transparent',
                            borderRadius: 1,
                            mb: 1,
                            '&:hover': {
                              backgroundColor: 'grey.100'
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Avatar 
                              sx={{ 
                                bgcolor: 'primary.main',
                                width: 36,
                                height: 36
                              }}
                            >
                              {comment.user_name.charAt(0).toUpperCase()}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {comment.user_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {comment.created_at?.toDate ? 
                                    new Date(comment.created_at.toDate()).toLocaleString() : 
                                    'Just now'
                                  }
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  mt: 0.5,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {comment.content}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </Fade>
                    ))}
                  </List>
                )}
              </Box>
              
              {/* Add Comment */}
              <Box 
                sx={{ 
                  borderTop: 1, 
                  borderColor: 'divider', 
                  pt: 2,
                  backgroundColor: 'background.paper'
                }}
              >
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Add a comment..."
                    variant="outlined"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        addComment();
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={addComment}
                    disabled={!newComment.trim()}
                    startIcon={<SendIcon />}
                    sx={{
                      minWidth: 'auto',
                      px: 3,
                      alignSelf: 'flex-end'
                    }}
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Keyboard Shortcuts Dialog */}
      <Dialog open={showShortcutsDialog} onClose={() => setShowShortcutsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Keyboard Shortcuts</DialogTitle>
        <DialogContent>
          <List>
            <ListItem>
              <ListItemText 
                primary="Ctrl/Cmd + N" 
                secondary="Create new note (Notepad tab) or task (Tasks tab)" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Ctrl/Cmd + /" 
                secondary="Focus search input" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Ctrl/Cmd + E" 
                secondary="Export current tab data" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Ctrl/Cmd + S" 
                secondary="Toggle select mode (Tasks tab only)" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Alt + 1-4" 
                secondary="Navigate between tabs" 
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Escape" 
                secondary="Close dialogs" 
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowShortcutsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default NotepadTasks;
